<template>
  <footer 
    id="footer" 
    class="footer"
    aria-label="Footer"
  >
    <!-- Footer Bottom Bar -->
    <div class="footer__bottom">
      <div class="footer__container">
        <div class="footer__bottom-content">
          <!-- Copyright -->
          <div class="footer__copyright" v-if="content && content.copyrightText">
            <p>{{ content.copyrightText }}</p>
          </div>

          <!-- Legal Links -->
          <div class="footer__legal-links" v-if="hasLegalLinks">
            <nav aria-label="Legal links">
              <ul class="footer__legal-nav">
                <li v-if="content.privacyPolicyUrl && content.privacyPolicyText">
                  <a 
                    :href="content.privacyPolicyUrl" 
                    class="footer__legal-link"
                  >
                    {{ content.privacyPolicyText }}
                  </a>
                </li>
                <li v-if="content.termsOfServiceUrl && content.termsOfServiceText">
                  <a 
                    :href="content.termsOfServiceUrl" 
                    class="footer__legal-link"
                  >
                    {{ content.termsOfServiceText }}
                  </a>
                </li>
              </ul>
            </nav>
          </div>

          <!-- Back to Top - Always show -->
          <button 
            @click="scrollToTop"
            aria-label="Back to top"
            class="footer__back-to-top"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 14L12 9L17 14H7Z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface FooterContent {
  copyrightText?: string
  termsOfServiceUrl?: string
  privacyPolicyUrl?: string
  termsOfServiceText?: string
  privacyPolicyText?: string
}

// Props
interface Props {
  content: FooterContent
}

const props = withDefaults(defineProps<Props>(), {
  content: () => ({})
})

// Computed properties
const hasLegalLinks = computed(() => {
  return (props.content.privacyPolicyUrl && props.content.privacyPolicyText) ||
         (props.content.termsOfServiceUrl && props.content.termsOfServiceText)
})

// Methods
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}
</script>

<style lang="scss" scoped>
.footer {
  background: linear-gradient(180deg, #1a0f0a 0%, #0f0804 100%);
  color: #cccccc;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%);
  }

  &__container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  // Footer Bottom
  &__bottom {
    padding: 24px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.3);
  }

  &__bottom-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;

    @media (max-width: 768px) {
      flex-direction: column;
      text-align: center;
      gap: 16px;
    }
  }

  &__copyright {
    font-size: 0.875rem;
    color: #999999;

    p {
      margin: 0;
    }
  }

  &__legal-links {
    @media (max-width: 768px) {
      order: -1;
    }
  }

  &__legal-nav {
    display: flex;
    list-style: none;
    padding: 0;
    margin: 0;
    gap: 24px;

    @media (max-width: 480px) {
      flex-direction: column;
      gap: 12px;
    }
  }

  &__legal-link {
    color: #999999;
    text-decoration: none;
    font-size: 0.875rem;
    transition: color 0.3s ease;

    &:hover {
      color: #ff6b35;
    }
  }

  &__back-to-top {
    width: 40px;
    height: 40px;
    background: rgba(255, 107, 53, 0.1);
    border: 1px solid rgba(255, 107, 53, 0.3);
    border-radius: 8px;
    color: #ff6b35;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 20px;
      height: 20px;
    }

    &:hover {
      background: rgba(255, 107, 53, 0.2);
      border-color: #ff6b35;
      transform: translateY(-2px);
    }
  }
}
</style>

